import useOverlay from "@/hooks/common/useOverlay";
import Alert from "@COMPONENTS/common/modal/alert/Desktop";
import {D_DELETE_EXCLUDE_KEYS, D_SHOW_KEYS} from "@COMPONENTS/product/common/popup/common/constant";
import * as style from "@COMPONENTS/style/productlist/departurepopup";
import {GridList} from "@FRAMEWORK/ui/desktop/filter";
import {Checkbox} from "@FRAMEWORK/ui/desktop/input";
import {TableContainer} from "@FRAMEWORK/ui/desktop/table";
import {GoodsDetail} from "@QUERIES/prdt/goods/useGoodsDetail";
import {GoodsOption} from "@QUERIES/prdt/goods/useGoodsOption";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {FilterType} from "./EventListPopup";

const EventListFilters = (prop: {
	filters: Array<FilterType>;
	setFilters: React.Dispatch<React.SetStateAction<Array<FilterType>>>;
	option: GoodsOption;
	goods: GoodsDetail;
}) => {
	const {filters, setFilters, option, goods} = prop;
	const {goodsCd, webDisplayCls} = goods;
	const goodsState = [
		{key: "isEvReserve", text: "예약가능", value: "true"},
		{key: "isEvConfirm", text: "출발확정", value: "true"},
	];
	const [open] = useOverlay();

	const handleFilters = (e, key: string) => {
		const {value, checked} = e.target;
		const text = e.target.parentNode.innerText;

		if (checked) {
			const copyArray = filters;
			const copyArrayExists = copyArray.some(item => item.value === value && item.key === key); /* 중복제거 */
			if (copyArrayExists) return;
			setFilters([...filters, {key, value, text}]);
		} else {
			if (key === "trCompanySnm" && filters.filter(v => v.key === "trCompanySnm").length === 1) {
				open(props => <Alert {...props} text="교통편은 최소 1개 이상 선택하셔야 합니다." />);
				return false;
			}
			if (key === "accomSeq" && filters.filter(v => v.key === "accomSeq").length === 1) {
				open(props => <Alert {...props} text="옵션은 최소 1개 이상 선택하셔야 합니다." />);
				return false;
			}
			const copyArray = filters;
			const setArray = copyArray.filter(item => !(item.value === value && item.key === key)); /* 동일한 value 제거 */
			setFilters(setArray);
		}
	};

	const handleFilterRemove = (key: string, value: string) => {
		const copyArray = filters;
		const setArray = copyArray.filter(item => {
			if (key === "price") {
				return !(item.key === "minPrice" || item.key === "maxPrice");
			} else {
				return !(item.value === value && item.key === key);
			}
		}); /* 동일한 value 제거 */
		setFilters(setArray);
	};

	const handleRemoveAll = () => {
		const setArray = filters.filter(v => D_DELETE_EXCLUDE_KEYS.includes(v.key));
		setFilters(setArray);
	};

	// 옵션 더보기 버튼
	const infoWrapRef = useRef(null);
	const infoContentRef = useRef(null);
	const [showText, setShowText] = useState(false);
	const [shouldShowButton, setShouldShowButton] = useState(false);

	const toggleText = () => {
		setShowText(!showText);
		setShouldShowButton(false);
	};

	useEffect(() => {
		const textHeight = infoContentRef.current?.clientHeight;
		const textWrapHeight = infoWrapRef.current?.clientHeight;

		if (textHeight > textWrapHeight) {
			setShouldShowButton(true);
		} else {
			setShouldShowButton(false);
		}
	}, [option]);

	// 선택된 필터
	const showFilter = useMemo(() => {
		let arr = [];
		for (const key of D_SHOW_KEYS) {
			if (key === "minPrice") {
				const minPrice = filters.find(v => v.key === "minPrice");
				if (minPrice) arr = [...arr, {key: "price", text: minPrice.text}];
			} else if (key === "maxPrice") {
				const maxPrice = filters.find(v => v.key === "maxPrice");
				const priceObject = arr.find(v => v.key === "price");
				if (priceObject && maxPrice) priceObject.text += ` ~ ${maxPrice.text}`;
			} else {
				arr = [...arr, ...filters.filter(v => v.key === key)];
			}
		}
		return arr;
	}, [filters]);

	return (
		<>
			{showFilter.length > 0 && (
				<style.FilterTagContainer>
					<style.FilterTitle>필터</style.FilterTitle>
					<style.FilterTagBox>
						<li>
							<style.ResetButton type="button" onClick={handleRemoveAll}>
								전체 삭제
							</style.ResetButton>
						</li>
						{showFilter.map((item, index) => (
							<li key={`${goodsCd}${index}`}>
								<style.FilterTag>
									{item.text}
									<style.FilterTagDeleteButton onClick={() => handleFilterRemove(item.key, item.value)}>
										닫기
									</style.FilterTagDeleteButton>
								</style.FilterTag>
							</li>
						))}
					</style.FilterTagBox>
				</style.FilterTagContainer>
			)}
			<style.FilterCheckWrap>
				<TableContainer lineTop types="basic">
					<caption>상품가 안내</caption>
					<colgroup>
						<col style={{width: "120px"}} />
						<col style={{width: "auto"}} />
					</colgroup>
					<tbody>
						<tr>
							<th>상품 상태</th>
							<td>
								<style.CheckBox>
									<GridList columns="repeat(4, 1fr)">
										{goodsState.map((item, index) => (
											<li key={index}>
												<Checkbox
													id={`state${goodsCd}${index}`}
													value={`${item.value}`}
													checked={filters.some(v => v.key === item.key) || false}
													onChange={e => handleFilters(e, item.key)}
												/>
												<label htmlFor={`state${goodsCd}${index}`}>{item.text}</label>
											</li>
										))}
									</GridList>
								</style.CheckBox>
							</td>
						</tr>
						{option?.trafficList && (
							<tr>
								<th>교통편</th>
								<td>
									<style.CheckBox>
										<GridList columns="repeat(4, 1fr)">
											{option.trafficList.map((item, index) => (
												<li key={index}>
													<Checkbox
														id={`airline${goodsCd}${index}`}
														value={`${item.trCompanySnm}`}
														checked={filters.some(v => v.value === item.trCompanySnm) || false}
														onChange={e => handleFilters(e, "trCompanySnm")}
													/>
													<label htmlFor={`airline${goodsCd}${index}`}>
														{item.trCompanySnm === "ETC" ? "교통불포함" : item.trCompanyNm}
													</label>
												</li>
											))}
										</GridList>
									</style.CheckBox>
								</td>
							</tr>
						)}

						{webDisplayCls === "B" && option?.accommodationList && (
							<tr>
								<th>옵션</th>
								<td>
									<style.CheckBox>
										<style.OptionCheckList show={showText} ref={infoWrapRef}>
											<GridList columns="1fr" ref={infoContentRef}>
												{option.accommodationList.map((item, index) => (
													<li key={index}>
														<Checkbox
															id={`option${goodsCd}${index}`}
															value={`${item.accomSeq}`}
															checked={filters.some(v => v.value === String(item.accomSeq)) || false}
															onChange={e => handleFilters(e, "accomSeq")}
														/>
														<label htmlFor={`option${goodsCd}${index}`}>{item.accomNm}</label>
													</li>
												))}
											</GridList>
										</style.OptionCheckList>
										{shouldShowButton && option.accommodationList.length > 3 && (
											<style.BenefitButton type="button" onClick={toggleText}>
												<span>더보기</span>
											</style.BenefitButton>
										)}
									</style.CheckBox>
								</td>
							</tr>
						)}
					</tbody>
				</TableContainer>
			</style.FilterCheckWrap>
		</>
	);
};

export default EventListFilters;
